import type { ColumnDef } from '@tanstack/react-table'
import { Badge } from '@/components/ui/badge'
import { DataTableColumnHeader } from '@/components/data-table'
import { formatDateTime } from '@/lib/format'

export type AdminFaqArticleRow = {
  id: string
  category_id: string
  slug: string
  title: string
  body_md: string
  sort_order: number
  published: boolean
  locale: string
  created_at: string
  updated_at: string
  category_title: string
  category_slug: string
}

export const ADMIN_FAQ_SORT_IDS = [
  'title',
  'sortOrder',
  'updatedAt',
  'createdAt',
  'published',
  'locale',
] as const

export const ADMIN_FAQ_FILTER_KEYS = [
  'published',
  'locale',
  'categoryId',
] as const

const PUBLISHED_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Published', value: 'published' },
  { label: 'Draft', value: 'draft' },
] as const

const LOCALE_OPTIONS = [
  { label: 'English', value: 'en' },
  { label: 'Spanish', value: 'es' },
] as const

export function createAdminFaqColumns(
  categoryOptions: { label: string; value: string }[] = [],
): ColumnDef<AdminFaqArticleRow, unknown>[] {
  return [
    {
      id: 'title',
      accessorKey: 'title',
      meta: { label: 'Title' },
      enableSorting: true,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Title" />
      ),
      cell: ({ row }) => (
        <div className="min-w-0 max-w-[16rem]">
          <div className="truncate text-sm font-medium">
            {row.original.title}
          </div>
          <div className="truncate font-mono text-[11px] text-muted-foreground">
            {row.original.slug}
          </div>
        </div>
      ),
    },
    {
      id: 'categoryId',
      accessorKey: 'category_id',
      meta: {
        label: 'Category',
        variant: 'select',
        options: categoryOptions,
      },
      enableSorting: false,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Category" />
      ),
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {row.original.category_title}
        </span>
      ),
    },
    {
      id: 'published',
      accessorKey: 'published',
      meta: {
        label: 'Status',
        variant: 'select',
        options: [...PUBLISHED_OPTIONS],
      },
      enableSorting: true,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Status" />
      ),
      cell: ({ row }) =>
        row.original.published ? (
          <Badge variant="success" className="font-normal">
            Published
          </Badge>
        ) : (
          <Badge variant="outline" className="font-normal">
            Draft
          </Badge>
        ),
    },
    {
      id: 'locale',
      accessorKey: 'locale',
      meta: {
        label: 'Locale',
        variant: 'select',
        options: [...LOCALE_OPTIONS],
      },
      enableSorting: true,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Locale" />
      ),
      cell: ({ row }) => (
        <span className="uppercase text-xs text-muted-foreground">
          {row.original.locale}
        </span>
      ),
    },
    {
      id: 'sortOrder',
      accessorKey: 'sort_order',
      meta: { label: 'Order' },
      enableSorting: true,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Order" />
      ),
      cell: ({ row }) => (
        <span className="tabular-nums text-sm text-muted-foreground">
          {row.original.sort_order}
        </span>
      ),
    },
    {
      id: 'updatedAt',
      accessorKey: 'updated_at',
      meta: { label: 'Updated' },
      enableSorting: true,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Updated" />
      ),
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-xs text-muted-foreground">
          {formatDateTime(row.original.updated_at)}
        </span>
      ),
    },
  ]
}
